import { useCallback, useEffect, useState } from "react";
import { css } from "@emotion/react";

import useForm from "../../../hooks/useForm";
import { FLAG_ZITADEL_MFA } from "../../../includes/featureFlags";
import { hasFeatureFlag } from "../../../includes/site";
import { useAppState } from "../../../contexts/AppContext";
import { AUTH_URL, getUrl, zitadelRoutes } from "../../../includes/routes";
import Button from "../../Button";
import Form from "../../form/Form";
import LabelledField from "../../form/LabelledField";
import Input from "../../form/Input";
import FormActions from "../../form/FormActions";
import { useZitadel, ZitadelSession } from "../../../contexts/ZitadelContext";
import { useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { FAILED_AUTHENTICATION_PARAM, zitadelGraphqlErrors } from "../../../includes/constants";
import { API_BASE_URL } from "../../../../config/config";
import Loading from "../../utils/Loading";

type ZitadelCompleteOtpEmailChallengeMutationResponse = {
    zitadelCompleteOtpEmailChallenge: {
        id: string
    };
}

type ZitadelCompleteOtpEmailChallengeMutationVariables = {
    sessionId: string;
    sessionToken: string;
    code: string;
}

type ZitadelStartOtpEmailRegistrationMutationResponse = {
    zitadelStartOtpEmailRegistration: ZitadelSession;
}

type ZitadelStartOtpEmailRegistrationMutationVariables = {
    sessionId: string;
    sessionToken: string;
}

const OtpEmailChallenge = () => {
    const navigate = useNavigate();

    const [isOtpEmailLoading, setIsOtpEmailLoading] = useState(false);
    const [isSendOtpEmailLoading, setIsSendOtpEmailLoading] = useState(false);
    const [isCancelLoading, setIsCancelLoading] = useState(false);
    const [isFallbackRedirectLoading, setIsFallbackRedirectLoading] = useState(false);

    const [appState] = useAppState();
    const isFeatureFlagsLoading = appState.isFeatureFlagsLoading;
    const hasZitadelMfaFlag = hasFeatureFlag(appState.featureFlags, FLAG_ZITADEL_MFA);

    const {
        loginInfo,
        setLoginInfo,
        clearLoginInfo,
        session,
        setSession,
        clearSession,
    } = useZitadel();

    const { formState, onFieldChange, onFieldInvalid, formErrors, setFormErrors } = useForm({
        fields: { code: "" },
    });

    const [zitadelStartOtpEmailRegistration] =
        useMutation<ZitadelStartOtpEmailRegistrationMutationResponse, ZitadelStartOtpEmailRegistrationMutationVariables>(
            ZITADEL_START_OTP_EMAIL_REGISTRATION,
        );

    const [zitadelCompleteOtpEmailChallenge] =
        useMutation<ZitadelCompleteOtpEmailChallengeMutationResponse, ZitadelCompleteOtpEmailChallengeMutationVariables>(
            ZITADEL_COMPLETE_OTP_EMAIL_CHALLENGE,
        );

    const onSubmit = useCallback(() => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register OTP email.");

        setIsOtpEmailLoading(true);

        zitadelCompleteOtpEmailChallenge({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
                code: formState.code,
            },
            fetchPolicy: 'network-only',
        }).then(() => {
            clearLoginInfo();
            clearSession();
            window.location.href = `${API_BASE_URL}/auth/login-redirect`;
        }).catch((e) => {
            const graphQlErrors = e.graphQLErrors;
            if (graphQlErrors) {
                if (
                    graphQlErrors.find(
                        (graphQlError: any) => graphQlError.extensions?.details?.includes(
                            zitadelGraphqlErrors.INVALID_MFA_CODE
                        )
                    )
                ) {
                    setFormErrors({ code: "Invalid code" });
                    // Screen reader announces a field error message.
                    const firstTargetKey = Object.keys(formErrors)?.[0] ?? null;
                    if (firstTargetKey) {
                        const firstElement = document.getElementsByName(Object.keys(formErrors)[0]);
                        if (firstElement.length > 0) {
                            firstElement[0].focus();
                        }
                    }
                    return;
                }
            }
            clearLoginInfo();
            clearSession();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    }, [
        clearLoginInfo,
        clearSession,
        formErrors,
        formState.code,
        navigate,
        session,
        setFormErrors,
        zitadelCompleteOtpEmailChallenge,
    ]);

    const onCancel = () => {
        setIsCancelLoading(true);
        setLoginInfo(prev => prev ? { ...prev, isOtpEmailChallengeRequired: false } : null);

        if (loginInfo?.isMfaRegistrationRequired) {
            /** Redirect to MFA initialisation if its still necessary */
            navigate(getUrl(zitadelRoutes.MFA_INITIALISATION.route));
        } else {
            /** Redirect to start of login flow otherwise */
            navigate(getUrl(AUTH_URL));
        }
    }

    const onSendOtpEmail = useCallback(() => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register email authentication.");

        setIsSendOtpEmailLoading(true);

        zitadelStartOtpEmailRegistration({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
            },
            fetchPolicy: 'network-only',
        }).then(({ data }) => {
            if (!data) throw new Error("OTP email registration did not return a valid response.");

            setIsSendOtpEmailLoading(false);
            setSession(data.zitadelStartOtpEmailRegistration);
        }).catch(() => {
            clearSession();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    }, [clearSession, navigate, session, setSession, zitadelStartOtpEmailRegistration]);

    useEffect(function fallbackRedirection() {
        /** SKip fallback redirection if actions are loading since the actions do the redirection themselves */
        if (isOtpEmailLoading || isSendOtpEmailLoading || isCancelLoading) return;

        if (!isFeatureFlagsLoading && !hasZitadelMfaFlag) {
            /** Redirect to start of login flow if missing required feature flags */
            setIsFallbackRedirectLoading(true);
            navigate(getUrl(AUTH_URL));
        }  else if (!loginInfo || !session) {
            /** Redirect to start of login flow if missing required variables */
            setIsFallbackRedirectLoading(true);
            navigate(getUrl(AUTH_URL));
        } else if (!loginInfo.isOtpEmailChallengeRequired) {
            /** Redirect if challenge is not required */
            if (loginInfo.isMfaRegistrationRequired) {
                /** Redirect to MFA initialisation if its still necessary */
                setIsFallbackRedirectLoading(true);
                navigate(getUrl(zitadelRoutes.MFA_INITIALISATION.route));
            } else {
                /** Redirect to start of login flow otherwise */
                setIsFallbackRedirectLoading(true);
                navigate(getUrl(AUTH_URL));
            }
        }
    }, [
        hasZitadelMfaFlag,
        isCancelLoading,
        isFeatureFlagsLoading,
        isOtpEmailLoading,
        isSendOtpEmailLoading,
        loginInfo,
        navigate,
        session,
    ]);

    const isLoading = isFeatureFlagsLoading || isFallbackRedirectLoading;
    const isActionsDisabled = isOtpEmailLoading || isSendOtpEmailLoading || isCancelLoading || isLoading;

    /** Blocking based on loginInfo & session is not actually necessary, but helps with typescript hinting */
    if (isLoading || !loginInfo || !session) return <Loading onTop overlay overlayColour={"white"} />;

    return (
        <div css={authContentStyle}>
            <div css={headerStyle}>
                <h1 css={leftHeaderStyle}>Multi-Factor Authentication</h1>
            </div>
            {loginInfo.isMfaRegistrationRequired && <p>Each time you log in, you will receive a one-time code.</p>}
            <h2>Step 1: Send one-time code</h2>
            {loginInfo.isMfaRegistrationRequired ? (
                <p>Click on the button below to send a one-time code to <b>{session.metadata.userEmail}</b></p>
            ) : (
                <p>
                    Your one-time code has been sent to <b>{session.metadata.userEmail}</b>.
                    <br/>
                    click the button below to receive another.
                </p>
            )}
            <div css={oneTimeCodeButtonContainerStyle}>
                <Button
                    css={buttonStyle}
                    type="button"
                    onClick={onSendOtpEmail}
                    disabled={isActionsDisabled}
                    forceLoading={isSendOtpEmailLoading}
                    useOutline
                >
                    Send a one-time code
                </Button>
            </div>
            <h2>Step 2: Get verification code</h2>
            <p>Enter the code you see you received in your email.</p>
            <Form onSubmit={onSubmit}>
                <LabelledField
                    name="code"
                    error={formErrors.code}
                    label="Authentication Code"
                    version={'normal'}
                >
                    <Input
                        hasError={!!formErrors.code}
                        label="Enter the code from your authenticator app"
                        name="code"
                        onChange={onFieldChange}
                        onInvalid={onFieldInvalid}
                        value={formState.code}
                        hasBorder
                        required
                    />
                </LabelledField>
                <div css={formActionsStyle}>
                    <FormActions customCss={customFormActionsStyles}>
                        {loginInfo.isMfaRegistrationRequired && (
                            <Button
                                css={cancelButtonStyle}
                                type="button"
                                onClick={onCancel}
                                disabled={isActionsDisabled}
                                forceLoading={isCancelLoading}
                                useOutline
                            >
                                Choose another method
                            </Button>
                        )}
                        <Button
                            css={buttonStyle}
                            type="submit"
                            disabled={isActionsDisabled}
                            forceLoading={isOtpEmailLoading}
                            useOutline
                        >
                            Confirm
                        </Button>
                    </FormActions>
                </div>
            </Form>
        </div>
    );
}

const ZITADEL_START_OTP_EMAIL_REGISTRATION = gql`
    mutation zitadelStartOtpEmailRegistration($sessionId: String!, $sessionToken: String!) {
        zitadelStartOtpEmailRegistration(sessionId: $sessionId, sessionToken: $sessionToken) {
            id
            token
            metadata {
                userEmail
            }
        }
    }
`;

const ZITADEL_COMPLETE_OTP_EMAIL_CHALLENGE = gql`
    mutation zitadelCompleteOtpEmailChallenge($sessionId: String!, $sessionToken: String!, $code: String!) {
        zitadelCompleteOtpEmailChallenge(sessionId: $sessionId, sessionToken: $sessionToken, code: $code) {
            id
        }
    }
`;

// Copied from SignIn.tsx
const authContentStyle = (theme: EmotionTheme) => css`
    margin-top: 40px;
    width: 510px;
    position: relative;

    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }

    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }
`;

// Copied from SignIn.tsx
const headerStyle = css`
    display: flex;
    justify-content: space-between;
`;

// Copied from SignIn.tsx
const leftHeaderStyle = (theme: EmotionTheme) => css`
    margin: 0;
    color: ${theme.colours.curiousBlue};
    font-weight: ${theme.fonts.weights.bold};

    ${theme.breakpoints.up("xxl")} {
        font-size: 50px;
        line-height: 75px;
    }

    ${theme.breakpoints.between("md", "xl")} {
        font-size: 43px;
        line-height: 65px;
    }

    ${theme.breakpoints.down("sm")} {
        font-size: 30px;
        line-height: 45px;
    }
`;

// Copied from SignIn.tsx
const formActionsStyle = (theme: EmotionTheme) => css`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    ${theme.breakpoints.up('sm')} {
        margin-top: 20px;
        justify-content: center;
    }

    ${theme.breakpoints.up("5xl")} {
        margin: 63px 0 68px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin: 41px 0 45px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin: 29px 0 34px;
    }

    ${theme.breakpoints.only("md")} {
        margin: 21px 0 26px;
    }

    ${theme.breakpoints.only("sm")} {
        margin: 26px 0 31px;
    }

    ${theme.breakpoints.down("xs")} {
        margin: 19px 0 24px;
    }
`;

// Copied from SignIn.tsx
const customFormActionsStyles = css`
    width: 100%;
    justify-content: center;
`;

// Copied from SignIn.tsx
const buttonStyle = (theme: EmotionTheme) => css`
    background-color: ${theme.colours.blue['400']};
    width: 400px;
    border-radius: 45px;
    height: 40px;
    padding: 0;

    ${theme.breakpoints.down('xs')} {
        width: 250px;
    }
`;
const cancelButtonStyle = (theme: EmotionTheme) => css`
    ${buttonStyle(theme)};
    background: none;
    color: ${theme.colours.blue['400']};
    border: 1px solid ${theme.colours.blue['400']};
`;

const oneTimeCodeButtonContainerStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
`;

export default OtpEmailChallenge;
